/**
 * Which engines compare database names case-insensitively.
 *
 * Consolidates three private copies of the same rule that had drifted apart
 * only in shape, never in substance — all three already agreed on the engine
 * set (`mssql`, `mysql`) and on treating an unset/empty engine as `mssql`
 * (a legacy saved record with no `engine` dials as MSSQL elsewhere too, e.g.
 * `preloadPriority.ts`'s `resolveHomePreloadDatabase`). The former sites:
 * - `connections/services/knownDatabasesTracker.ts` (`CASE_INSENSITIVE_ENGINES` + `caseFoldFor`)
 * - `connection-diagnostics/hooks/useFallbackPresentation.ts` (`CASE_INSENSITIVE_ENGINES` + `caseFold`)
 * - `database-objects/services/preloadPriority.ts` (`CASE_INSENSITIVE_ENGINES` + `caseFoldFor`)
 *
 * Dependency-free on purpose (no `DatabaseEngineId` import) so it stays usable
 * from anywhere, including main-process code, without pulling in renderer types.
 */

/**
 * Engines that fold identifiers case-insensitively.
 *
 * **MySQL is listed unconditionally, and that is only half true.** MySQL's
 * database-name comparison is decided by the SERVER's `lower_case_table_names`:
 * `0` (the Linux default) makes database and table names case-SENSITIVE, while
 * `1` (the Windows default and the usual macOS one) and `2` fold them. So on a
 * stock Linux server `MyDb` and `mydb` are two different databases, and this
 * rule says they are one.
 *
 * It is inert in practice at every current call site, because each one takes an
 * EXACT match first and only reaches the fold as a fallback: the known-database
 * tracker appends a name it just saw succeed, the diagnostics fallback list and
 * the preload priority both rank a name they were handed against a list that
 * contains it verbatim. The fold can therefore only collapse two entries that
 * differ in case — a shape a case-sensitive server would have to be hosting on
 * purpose — and never picks the wrong database out of a list that holds the
 * right one.
 *
 * Follow-up if that stops being true (a call site that resolves a name rather
 * than confirming one): read `lower_case_table_names` from the server at
 * connect time — every MySQL manager already runs a session-setup query where
 * `SELECT @@lower_case_table_names` costs nothing — cache it on the connection
 * and make this predicate take it. Until then a per-connection flag would be
 * threaded through three call sites to change nothing.
 */
export const CASE_INSENSITIVE_ENGINES = new Set(['mssql', 'mysql']);

/**
 * Whether `engine` folds identifiers case-insensitively. An unset/empty
 * engine is treated as `mssql` — the same default every caller of this rule
 * already applied for a legacy saved record with no `engine` field.
 */
export function isCaseInsensitiveDatabaseEngine(engine?: string): boolean {
  return CASE_INSENSITIVE_ENGINES.has((engine || 'mssql').toLowerCase());
}

/** The identity function to compare two database names on this engine. */
export function caseFoldFor(engine?: string): (name: string) => string {
  return isCaseInsensitiveDatabaseEngine(engine) ? (name) => name.toLowerCase() : (name) => name;
}
